import React, { memo, useEffect, useState } from 'react';
import styles from './styles.module.css';
import { Table, Loading, ConfirmModal } from '../../../../../common/ui';
import { getShipments, retryShipment } from '../../actions';
import { showAlert } from '../../../../../common/actions';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { PiSmileySad } from 'react-icons/pi';

const RETRYABLE_STATUSES = ['FAILED', 'INITIATED'];

const ShipmentTable = memo(
  ({
    getShipments,
    retryShipment,
    showAlert,
    shipmentsState: { shipmentsData, isLoading },
    auth: { branchId },
    filterParams,
    searchTerm = '',
    fromDate,
    toDate,
  }) => {
    const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
    const [shipmentIdToRetry, setShipmentIdToRetry] = useState(null);

    const buildQueryParams = () => {
      const transformedParams = {
        ...(filterParams?.trackingNo?.id
          ? { 'delivery.id': filterParams.trackingNo.id }
          : {}),
      };
      if (searchTerm && searchTerm.trim()) {
        transformedParams.keyword = searchTerm.trim();
      }
      if (fromDate) {
        transformedParams.fromDate = fromDate;
      }
      if (toDate) {
        transformedParams.toDate = toDate;
      }
      return transformedParams;
    };

    useEffect(() => {
      if (!branchId) return;
      getShipments(buildQueryParams());
    }, [filterParams, branchId, searchTerm, fromDate, toDate, getShipments]);

    const handleRetryClick = (id) => {
      const shipment = (shipmentsData || []).find((row) => row.id === id);
      if (!RETRYABLE_STATUSES.includes(shipment?.status)) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Only failed or initiated shipments can be retried.',
        });
        return;
      }

      setShipmentIdToRetry(id);
      setRetryConfirmOpen(true);
    };

    const handleCloseRetryConfirm = () => {
      setRetryConfirmOpen(false);
      setShipmentIdToRetry(null);
    };

    const handleConfirmRetry = async () => {
      const id = shipmentIdToRetry;
      if (id == null) return;

      const result = await retryShipment(id);
      if (result?.success && result?.data?.status === 'PENDING') {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: `Shipment retried successfully. Tracking No: ${result.data.trackingNo}`,
        });
      } else if (result?.success) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Retry failed. The shipment remains in FAILED status.',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: result?.error || 'Failed to retry shipment.',
        });
      }

      getShipments(buildQueryParams());
    };

    const columns = [
      { id: 'orderNo', label: 'Order No', minWidth: 50 },
      { id: 'partnerName', label: 'Partner', minWidth: 100 },
      { id: 'trackingNo', label: 'Tracking Number', minWidth: 100 },
      { id: 'recipientName', label: 'Recipient Name', minWidth: 150 },
      { id: 'deliveryAddress', label: 'Delivery Address', minWidth: 200 },
      { id: 'status', label: 'Status', minWidth: 50 },
      { id: 'expectedAmount', label: 'Expected Amount', minWidth: 50 },
      { id: 'collectedAmount', label: 'Collected Amount', minWidth: 50 },
      {
        id: 'options',
        minWidth: 20,
        options: ['Update', 'Retry', 'Delete'],
        table: 'shipment',
        customHandlers: {
          onRetry: handleRetryClick,
        },
      },
    ];

    let tableContent;
    if (isLoading) {
      tableContent = (
        <div className={styles.loadingWrap}>
          <Loading variant="inline" label="" />
        </div>
      );
    } else if (!shipmentsData || shipmentsData.length === 0) {
      tableContent = (
        <div className={styles.noData}>
          <PiSmileySad className={styles.noDataIcon} aria-hidden />
          <p>No shipment data available.</p>
        </div>
      );
    } else {
      tableContent = <Table columns={columns} rows={shipmentsData} />;
    }

    return (
      <div className={styles.wrapper}>
        {tableContent}
        <ConfirmModal
          open={retryConfirmOpen}
          onClose={handleCloseRetryConfirm}
          onConfirm={handleConfirmRetry}
          title="Retry shipment"
          message="Retry placing this shipment with the delivery partner? This may create a duplicate order if the partner already received it."
          confirmLabel="Retry"
          cancelLabel="Cancel"
        />
      </div>
    );
  }
);

ShipmentTable.propTypes = {
  getShipments: PropTypes.func.isRequired,
  retryShipment: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
  shipmentsState: PropTypes.shape({
    shipmentsData: PropTypes.array,
    isLoading: PropTypes.bool,
  }).isRequired,
  auth: PropTypes.shape({
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  filterParams: PropTypes.object,
  searchTerm: PropTypes.string,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
};

const mapStateToProps = (state) => ({
  shipmentsState: state.shipmentsState,
  auth: state.authState,
});

export default connect(mapStateToProps, { getShipments, retryShipment, showAlert })(ShipmentTable);
